import { DataGrid, EmptyState, Icon, theme, Spinner } from '@ketch-com/deck'
import { Box, Typography } from '@mui/material'
import { useDataSentryEventsList } from 'api/dataSentry/queries/useDataSentryEventsList'
import { useDataSentryContext } from '../context/DataSentryContext'
import { useMemo, useRef, useState } from 'react'
import { DataSentryTableFilters } from '../components/DataSentryTableFilters'
import { dataSentryEventsDataGridUtil } from './utils/DataSentryEventsDataGridUtil'
import { useTableFilters } from '../hooks/useTableFilters'
import { DataSummaryWidget } from './dataSummaryWidget/DataSummaryWidget'
import {
  AuthorizationStatus as TrackerAuthorizationStatus,
  ControlStatus as TrackerControlStatus,
} from '@ketch-com/windlass/dist/dataSentry/v1/events/events'

export const DataSentryEventsTable: React.FC = () => {
  const [trackerAuthorizationStatus, setTrackerAuthorizationStatus] = useState<TrackerAuthorizationStatus>(
    TrackerAuthorizationStatus.AUTHORIZED,
  )
  const [trackerControlStatus, setTrackerControlStatus] = useState<TrackerControlStatus>(
    TrackerControlStatus.UNSPECIFIED,
  )
  const {
    searchQuery,
    debouncedSearchQuery,
    setSearchQuery,
    dataSystemFilterValue,
    setDataSystemFilterValue,
    paginationModel,
    setPaginationModel,
  } = useTableFilters()

  const {
    selectedTimeRange,
    selectedPropertyCode,
    dataTypesTotalCount,
    setDataClassificationDetails,
    hasPropertyAndScanTimeRange,
    setNetworkDetails,
  } = useDataSentryContext()
  const { data: eventsList, isLoading } = useDataSentryEventsList({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: debouncedSearchQuery,
      'data-system-code': dataSystemFilterValue?.code,
      'authorization-status': trackerAuthorizationStatus,
      'control-status': trackerControlStatus,
    },
    enabled: hasPropertyAndScanTimeRange,
  })
  const rowCountRef = useRef(eventsList?.total || 0)
  const rowCount = useMemo(() => {
    if (eventsList?.total !== undefined) {
      rowCountRef.current = eventsList?.total
    }
    return rowCountRef.current
  }, [eventsList?.total])

  const columns = useMemo(() => {
    return dataSentryEventsDataGridUtil({ dataTypesTotalCount, setDataClassificationDetails, setNetworkDetails })
  }, [dataTypesTotalCount, setDataClassificationDetails, setNetworkDetails])

  const isLoadingEventsList = isLoading
  const displayDataGridEmptyState = !isLoadingEventsList && !eventsList?.events?.length && !debouncedSearchQuery
  const displayDataGrid =
    !isLoadingEventsList && (eventsList?.events?.length || (!eventsList?.events?.length && debouncedSearchQuery))

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{
        backgroundColor: theme.palette.Common.Container,
      }}
      borderRadius={4}
      px={2.5}
      py={2}
    >
      <Typography pb={1} variant="h4">
        Data-Collecting Network Requests
      </Typography>

      <DataSummaryWidget />

      <DataSentryTableFilters
        searchQuery={searchQuery}
        handleSearchInputChange={setSearchQuery}
        dataSystemFilterValue={dataSystemFilterValue}
        setDataSystemFilterValue={setDataSystemFilterValue}
        trackerAuthorizationStatus={trackerAuthorizationStatus}
        setTrackerAuthorizationStatus={setTrackerAuthorizationStatus}
        trackerControlStatus={trackerControlStatus}
        setTrackerControlStatus={setTrackerControlStatus}
        setPaginationModel={setPaginationModel}
      />
      {isLoadingEventsList ? (
        <Box display="flex" width="100%" alignItems="center" justifyContent="center" height={350}>
          <Spinner size="32px" thickness={2.5} />
        </Box>
      ) : null}

      {displayDataGridEmptyState ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="350px">
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
            customIcon={<Icon name="OTableView" width="48px" height="48px" iconColor={theme.palette.Black.o48} />}
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        </Box>
      ) : null}

      {displayDataGrid ? (
        <DataGrid
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
              backgroundImage: 'unset',
              cursor: 'auto',
            },
            ...(isLoadingEventsList
              ? {
                  '& .MuiDataGrid-virtualScroller': {
                    height: '300px',
                  },
                }
              : {}),
          }}
          autosizeOnMount
          autosizeOptions={{
            expand: true,
            includeHeaders: true,
            includeOutliers: false,
          }}
          getRowHeight={() => 'auto'}
          columns={columns}
          getRowId={row => row.url}
          rows={eventsList?.events || []}
          checkboxSelection={false}
          hideFooter={false}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnPinning
          disableColumnReorder
          disableColumnResize
          disableRowHoverStates
          disableChildrenSorting={false}
          loading={isLoadingEventsList}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          noRowsOverlayProps={{ buttonTitle: '' }}
        />
      ) : null}
    </Box>
  )
}
